import { useState, useMemo } from "react";

interface ApprovedUser {
  name: string;
  tags: string[];
}

interface AccessRecord {
  granted_to_user: ApprovedUser;
  types: string[];
}

type AccessType =
  | "parent/guardian"
  | "other family"
  | "friend"
  | "emergency contact"
  | "clinician/medical professional"
  | "school";

const TagStyle = {
  "parent/guardian": "bg-purple-100 text-purple-700",
  "other family": "bg-purple-100 text-purple-700",
  friend: "bg-pink-100 text-pink-700",
  "emergency contact": "bg-orange-100 text-orange-700",
  "clinician/medical professional": "bg-pink-100 text-pink-700",
  school: "bg-pink-100 text-pink-700",
} as const;

export const ApprovedUsers = ({
  accessRecords,
}: {
  accessRecords: AccessRecord[];
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const users = useMemo(() => {
    return (
      accessRecords?.map((record) => ({
        name: record.granted_to_user.name,
        tags: record.types.map((type) => type.toLowerCase().replace(/_/g, " ")),
      })) ?? []
    );
  }, [accessRecords]);

  const getTagStyle = (tag: AccessType): string => {
    return TagStyle[tag] ?? TagStyle["friend"]; // Using friend style as default
  };

  return (
    <div className="rounded-lg border p-4">
      <button
        className="flex w-full items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h2 className="text-lg font-semibold">Approved Users</h2>
        <span>{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="mt-4 grid grid-cols-3 gap-6">
          {users?.length > 0 ? (
            users.map((user, index) => (
              <div key={index} className="flex flex-col">
                <span className="mb-2 font-medium">{user.name}</span>
                <div className="flex flex-col items-start gap-1">
                  {user.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className={`w-fit rounded-full px-2 py-1 text-sm ${getTagStyle(tag as AccessType)}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div>No approved users found</div>
          )}
        </div>
      )}
    </div>
  );
};
